import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Car, ShieldCheck, Star, Lock, MapPin, Phone, Calendar, CheckCircle2, XCircle, Mountain, Snowflake, TreePine, Building2, Sunrise, Coffee, MessageCircle, Sparkles, Award, BadgeCheck, FileText, Headphones, Fuel, RotateCcw, User, Shield, FileWarning, Wrench, Clock } from 'lucide-react';
import { PageHero } from './DestinationsPages';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import { SELF_DRIVE_CARS, SELF_DRIVE_ROUTES, SELF_DRIVE_PICKUPS, SELF_DRIVE_FAQS, COMPANY, saveEnquiry } from '../mock';
import EnquiryForm from '../components/sections/EnquiryForm';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';

const ROUTE_ICONS = { Mountain, Snowflake, TreePine, Building2, Sunrise, Coffee };
const CAT_LABELS = { '4x4': '🏔️ 4×4 & Adventure', suv: '🚘 SUVs & Premium SUVs', sedan: '🚙 Sedans & Hatchbacks', luxury: '💎 Luxury Cars (On Request)' };
const CAT_ORDER = ['4x4', 'suv', 'sedan', 'luxury'];

export default function SelfDrivePage() {
  return (
    <div>
      <Helmet>
        <title>Self Drive Car Rentals | Mountains Chain Travel</title>
        <meta name="description" content="Rent a 4x4 or SUV for self-drive in Himachal, Ladakh, and Spiti. Reliable fleet, competitive prices, and 24x7 roadside assistance." />
        <meta property="og:title" content="Self Drive Car Rentals | Mountains Chain Travel" />
        <meta property="og:description" content="Rent a 4x4 or SUV for self-drive in Himachal, Ladakh, and Spiti. Reliable fleet, competitive prices, and 24x7 roadside assistance." />
      </Helmet>
      <SelfDriveHero />
      <CustomerVideoReviews />
      <BookingSection />
      <StatsStrip />
      <AboutSection />
      <FleetSection />
      <PricingTables />
      <ProcessSection />
      <DocsAndDeposit />
      <SafetySection />
      <PoliciesGrid />
      <IncludedNotIncluded />
      <RoutesSection />
      <CredentialsSection />
      <TestimonialsSection />
      <PickupSection />
      <FAQSection />
      <FinalCTA />
      <EnquiryForm />
    </div>
  );
}

function SelfDriveHero() {
  return (
    <section className="relative bg-brand-blue text-white pt-12 pb-16 overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 20%, #FF6B00 0%, transparent 35%), radial-gradient(circle at 80% 80%, #13345b 0%, transparent 40%)'}}/>
      <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-brand-orange/20 blur-3xl"/>
      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full text-[12px] font-semibold mb-5">
          <Lock size={13} className="text-brand-orange"/> Ministry of Tourism Authorized · HP Tourism Registered
        </div>
        <h1 className="font-poppins font-extrabold text-[40px] lg:text-[56px] leading-[1.05] tracking-tight">
          Self-Drive Car Rentals in <span className="text-brand-orange">Himachal Pradesh</span>
        </h1>
        <p className="mt-4 text-white/85 text-[15px] lg:text-[17px] font-medium">Shimla · Manali · Dharamshala · Kasauli · Spiti · Leh Road</p>
        <p className="mt-3 text-white/65 text-[14.5px] max-w-2xl mx-auto leading-relaxed">Drive your own adventure across the Himalayas. Verified vehicles, flexible pickups, no hidden charges - just open roads and mountain air.</p>
        <div className="mt-7 flex flex-wrap gap-3 justify-center">
          <a href="#sd-booking"><Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-6 rounded-xl font-semibold btn-glow"><Car size={17} className="mr-2"/>Book Your Self-Drive Car</Button></a>
          <a href="#sd-fleet"><Button variant="outline" className="bg-white/10 backdrop-blur-md border-white/40 text-white hover:bg-white hover:text-brand-blue h-12 px-6 rounded-xl font-semibold">View All Cars →</Button></a>
        </div>
        <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
          {[
            {icon: Award, t: 'Ministry of Tourism Auth.'},
            {icon: Star, t: '5-Star Google Rating'},
            {icon: BadgeCheck, t: 'No Hidden Charges'},
            {icon: ShieldCheck, t: 'Fully Insured Vehicles'},
          ].map((x, i) => (
            <div key={i} className="flex items-center gap-2 bg-white/8 px-3 py-2.5 rounded-lg text-[12.5px] font-semibold">
              <x.icon size={15} className="text-brand-orange shrink-0"/>{x.t}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BookingSection() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', pickup: '', startDate: '', endDate: '', carType: '', days: '' });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup) {
      toast({ title: 'Please fill required fields', description: 'Name, phone & pickup are required.' });
      return;
    }
    saveEnquiry({ ...form, type: 'self-drive' });
    toast({ title: '✨ Booking request received!', description: 'Our team will WhatsApp you within 5 minutes with availability & pricing.' });
    setForm({ name: '', phone: '', pickup: '', startDate: '', endDate: '', carType: '', days: '' });
  };

  return (
    <section id="sd-booking" className="max-w-5xl mx-auto px-6 -mt-12 relative z-10">
      <form onSubmit={submit} className="bg-white rounded-3xl shadow-premium border border-brand-greyDark p-7 lg:p-9 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-orange/10"/>
        <div className="relative">
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <div>
              <h3 className="font-poppins font-bold text-brand-blue text-[22px]">Book Your Self-Drive Car</h3>
              <p className="text-brand-blue/60 text-[13px] mt-1">Instant confirmation ⚡</p>
            </div>
            <div className="text-[12px] text-brand-blue/55 flex items-center gap-1.5"><Phone size={13}/>Or call <a href={`tel:${COMPANY.phone}`} className="text-brand-orange font-semibold">{COMPANY.phone}</a></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full Name *"><Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="e.g., Rohan Sharma" className="h-11 rounded-lg"/></Field>
            <Field label="Phone Number *"><Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 9XXXXXXXXX" className="h-11 rounded-lg"/></Field>
            <Field label="Pickup Location *">
              <select value={form.pickup} onChange={e => update('pickup', e.target.value)} className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40">
                <option value="">Select Pickup City</option>
                {SELF_DRIVE_PICKUPS.map(p => <option key={p} value={p}>{p}</option>)}
                <option value="other">Other (Call Us)</option>
              </select>
            </Field>
            <Field label="Car Type">
              <select value={form.carType} onChange={e => update('carType', e.target.value)} className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40">
                <option value="">Select Car Category</option>
                <option value="4x4">4×4 / Adventure (₹2,500–₹4,500/day)</option>
                <option value="suv">SUV / Premium SUV (₹2,000–₹3,500/day)</option>
                <option value="sedan">Sedan (₹2,800–₹4,500/day)</option>
                <option value="hatchback">Hatchback (₹1,800–₹2,800/day)</option>
                <option value="luxury">Luxury - On Request</option>
              </select>
            </Field>
            <Field label="Start Date *"><Input type="date" value={form.startDate} onChange={e => update('startDate', e.target.value)} className="h-11 rounded-lg"/></Field>
            <Field label="End Date"><Input type="date" value={form.endDate} onChange={e => update('endDate', e.target.value)} className="h-11 rounded-lg"/></Field>
            <div className="md:col-span-2"><Field label="No. of Days"><Input type="number" min="1" value={form.days} onChange={e => update('days', e.target.value)} placeholder="e.g., 5" className="h-11 rounded-lg"/></Field></div>
          </div>

          <Button type="submit" className="w-full mt-6 bg-brand-orange hover:bg-brand-orangeDark text-white h-12 rounded-xl font-semibold btn-glow"><Car size={17} className="mr-2"/>Check Availability & Price</Button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-semibold text-brand-blue/75 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function StatsStrip() {
  const stats = [
    { v: '500+', l: 'Verified Vehicles' },
    { v: '10K+', l: 'Happy Customers' },
    { v: '5★', l: 'Google Rating' },
    { v: '12+', l: 'Pickup Locations' },
    { v: '₹1,800', l: 'Starting / Day' },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 mt-16">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-brand-greyDark rounded-2xl overflow-hidden border border-brand-greyDark">
        {stats.map((s, i) => (
          <div key={i} className="bg-white p-6 text-center">
            <div className="font-poppins font-bold text-[28px] lg:text-[34px] text-brand-blue leading-none">{s.v}</div>
            <div className="text-brand-blue/60 text-[12px] mt-1.5 font-medium">{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">About Us</div>
        <h2 className="font-poppins text-[30px] lg:text-[38px] text-brand-blue leading-[1.1]">Mountains Chain Travel <span className="text-brand-orange">Pvt. Ltd.</span></h2>
        <p className="text-brand-blue/70 text-[15px] mt-4 leading-relaxed">Mountains Chain Travel Pvt. Ltd. brings a safe, transparent and premium self-drive experience across Himachal Pradesh. We offer well-maintained, verified and tour-ready vehicles - perfect for families, couples, solo travelers and adventure explorers.</p>
      </div>
      <div className="bg-brand-grey rounded-2xl p-6 lg:p-7">
        <div className="font-poppins font-semibold text-brand-blue text-[17px] mb-4">Why Self-Drive With Us</div>
        <ul className="space-y-2.5">
          {['Wide range of 4×4, SUVs & luxury vehicles','Complete privacy & freedom','Easy pickup/drop anywhere in Himachal','GPS-enabled vehicles','Fully sanitized before each journey','24×7 roadside assistance','Transparent billing - no hidden charges'].map((x, i) => (
            <li key={i} className="flex items-start gap-2.5 text-brand-blue/80 text-[14.5px]"><CheckCircle2 size={17} className="text-emerald-600 mt-0.5 shrink-0"/>{x}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FleetSection() {
  return (
    <section id="sd-fleet" className="max-w-7xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Available Vehicle Categories</div>
        <h2 className="font-poppins text-[30px] lg:text-[40px] text-brand-blue leading-[1.1]">Our Self-Drive Fleet - <span className="text-brand-orange">For Every Road</span></h2>
        <p className="text-brand-blue/60 text-[13.5px] mt-3">All vehicles are GPS-tracked, fully insured & mountain-road certified</p>
      </div>
      {CAT_ORDER.map(cat => {
        const cars = SELF_DRIVE_CARS.filter(c => c.cat === cat);
        return (
          <div key={cat} className="mb-10">
            <div className="font-poppins font-semibold text-brand-blue text-[17px] mb-4">{CAT_LABELS[cat]}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {cars.map(car => (
                <div key={car.name} className="bg-white rounded-2xl overflow-hidden border border-brand-greyDark shadow-card card-hover img-zoom-wrap">
                  <div className="aspect-[4/3] overflow-hidden bg-brand-grey"><img src={car.image} alt={car.name} className="w-full h-full object-cover img-zoom" loading="lazy"/></div>
                  <div className="p-4">
                    <div className="font-poppins font-semibold text-brand-blue text-[14.5px] leading-tight">{car.name}</div>
                    <div className="text-brand-orange text-[12.5px] font-bold mt-1">{car.price}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function PricingTables() {
  const tables = [
    { title: '1. Hatchbacks - Basic Segment', sub: 'For city travel, couples, local sightseeing', rows: [['Alto | Kwid', '₹1,800 – ₹2,200'], ['Wagon-R | Celerio', '₹2,000 – ₹2,400'], ['Swift | Baleno | i20', '₹2,200 – ₹2,800']] },
    { title: '2. Sedans - Comfort Segment', sub: 'For long routes, comfortable trips', rows: [['Dzire | Amaze', '₹2,800 – ₹3,500'], ['Honda City | Verna', '₹3,500 – ₹4,500']] },
    { title: '3. Compact SUVs - Most Demanded', sub: 'Best for Shimla–Manali–Spiti routes', rows: [['Creta | Seltos', '₹2,200 – ₹3,000'], ['Kushaq | Taigun', '₹2,500 – ₹3,200'], ['XUV 700', '₹3,000 – ₹3,800']] },
    { title: '4. 4×4 & Adventure Vehicles', sub: 'For Spiti, Rohtang, off-road adventures', rows: [['Mahindra Thar', '₹2,500 – ₹3,500'], ['Force Gurkha', '₹3,000 – ₹4,000'], ['Fortuner / Hilux', '₹3,500 – ₹4,500'], ['Thar Roxx', '₹3,500 – ₹4,500']] },
  ];
  return (
    <section className="bg-brand-grey py-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">2026 Updated Pricing</div>
          <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Self-Drive Car <span className="text-brand-orange">Per Day Pricing</span></h2>
          <p className="text-brand-blue/60 text-[13.5px] mt-2">Transparent rates · No hidden charges · GST included</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {tables.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-brand-greyDark shadow-card">
              <div className="font-poppins font-semibold text-brand-blue text-[16px]">{t.title}</div>
              <div className="text-brand-blue/55 text-[12.5px] mt-1 mb-4">{t.sub}</div>
              <div className="space-y-2">
                {t.rows.map(([v, p], j) => (
                  <div key={j} className="flex items-center justify-between py-2 border-b border-brand-grey last:border-0">
                    <div className="text-brand-blue/80 text-[14px]">{v}</div>
                    <div className="text-brand-orange font-semibold text-[13.5px]">{p}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-brand-blue/55 text-[12px] mt-5 text-center">* Prices vary by season, availability & pickup location. Call us for exact quotes.</p>
      </div>
    </section>
  );
}

function ProcessSection() {
  const steps = [
    {t: 'Choose Vehicle', d: 'Browse our fleet and select the right car for your trip.'},
    {t: 'Submit KYC Documents', d: 'Driving licence, ID proof & required documents.'},
    {t: 'Pay Token Advance', d: 'Confirm your booking with a token advance payment.'},
    {t: 'Vehicle Inspection', d: 'Joint inspection at pickup. Sign rental agreement.'},
    {t: 'Start Your Trip', d: 'Keys in hand - hit the mountain roads!'},
    {t: 'Return & Final Check', d: 'Return vehicle. Final inspection & deposit refund same day.'},
    {t: 'Refund Deposit', d: 'Security deposit refunded after inspection within 6–24 hrs.'},
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Simple & Quick</div>
        <h2 className="font-poppins text-[30px] lg:text-[38px] text-brand-blue leading-[1.1]">Rental <span className="text-brand-orange">Process</span></h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <div key={i} className="relative bg-white border border-brand-greyDark rounded-2xl p-5 card-hover">
            <div className="absolute -top-3.5 left-5 w-9 h-9 rounded-xl bg-brand-orange text-white font-poppins font-bold flex items-center justify-center shadow-glow-orange text-[15px]">{i+1}</div>
            <div className="font-poppins font-semibold text-brand-blue text-[15.5px] mt-3">{s.t}</div>
            <div className="text-brand-blue/65 text-[13px] mt-1.5 leading-relaxed">{s.d}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DocsAndDeposit() {
  return (
    <section className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-5 pb-12">
      <div className="bg-white border border-brand-greyDark rounded-2xl p-6">
        <div className="flex items-center gap-2 text-brand-blue font-poppins font-semibold text-[17px] mb-4"><FileText size={18} className="text-brand-orange"/>Required Documents</div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <div className="text-[12px] font-bold text-brand-orange tracking-wider uppercase mb-2">For Indian Customers</div>
            <ul className="space-y-1.5 text-brand-blue/75 text-[13.5px]">
              {['Aadhaar Card','Valid Driving Licence','Address Proof','Security Deposit'].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0"/>{x}</li>)}
            </ul>
          </div>
          <div>
            <div className="text-[12px] font-bold text-brand-orange tracking-wider uppercase mb-2">For Foreign Customers</div>
            <ul className="space-y-1.5 text-brand-blue/75 text-[13.5px]">
              {['Passport','International Driving Permit','Visa & Entry Stamp','Refundable Deposit'].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0"/>{x}</li>)}
            </ul>
          </div>
        </div>
      </div>
      <div className="bg-brand-blue text-white rounded-2xl p-6">
        <div className="flex items-center gap-2 font-poppins font-semibold text-[17px] mb-4"><Lock size={18} className="text-brand-orange"/>Security Deposit & Payment Terms</div>
        <ul className="space-y-2 text-white/80 text-[13.5px]">
          {['Deposit: ₹5,000 – ₹50,000 (depending on vehicle)','50% advance booking amount required','One undated blank cheque for security','Remaining balance at pickup','Deposit refunded after inspection within 6–24 hours'].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={15} className="text-brand-orange mt-0.5 shrink-0"/>{x}</li>)}
        </ul>
      </div>
    </section>
  );
}

function SafetySection() {
  return (
    <section className="bg-brand-grey py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Safety & Rules</div>
          <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Vehicle Inspection & <span className="text-brand-orange">Driving Guidelines</span></h2>
        </div>
        <div className="grid lg:grid-cols-3 gap-5">
          <Card icon={ShieldCheck} title="Vehicle Inspection Checklist">
            {['Tyres (including spare)','Fuel level','Brakes','Lights & indicators','Seat belts','Toolkit, jack & accessories','Car documents verified','Car body photos (customer + staff)']}
          </Card>
          <Card icon={Mountain} title="Driving Rules - Himachal Specific">
            {['No overspeeding on hills','Avoid driving after 8 PM in snow zones','Chains compulsory in snow (if required)','No off-roading unless permitted',"Don't leave vehicle unattended on slopes",'Follow local police / taxi union rules','Drive responsibly on narrow roads']}
          </Card>
          <Card icon={Wrench} title="Breakdown & Emergency Support">
            {['24×7 helpline available','Towing support','On-spot mechanic assistance','Replacement vehicle (subject to availability)']}
          </Card>
        </div>
      </div>
    </section>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-brand-greyDark shadow-card">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-10 h-10 rounded-xl bg-brand-orange/10 text-brand-orange flex items-center justify-center"><Icon size={18}/></div>
        <div className="font-poppins font-semibold text-brand-blue text-[15.5px]">{title}</div>
      </div>
      <ul className="space-y-2 text-brand-blue/75 text-[13.5px]">
        {children.map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0"/>{x}</li>)}
      </ul>
    </div>
  );
}

function PoliciesGrid() {
  const items = [
    {icon: Fuel, t: 'Fuel Policy', list: ['Full-to-Full or As-is (return with same fuel level)','Fuel charges not included in rental']},
    {icon: Car, t: 'Vehicle Return Conditions', list: ['Clean interior required','No smoking smell','All documents & accessories intact','Same fuel level as pickup','No new dents / damages - damage deducted from deposit']},
    {icon: RotateCcw, t: 'Cancellation & Refund Policy', list: ['100% refund if cancelled 72 hrs before','50% refund before 48 hrs','No refund within 24 hrs','No-show = No refund']},
    {icon: User, t: 'Customer Responsibilities', list: ['Safe driving at all times','No illegal activities','No drunk driving','Traffic challans paid by customer','Car not to be sub-rented','Respect local road rules']},
    {icon: Shield, t: 'Insurance Coverage', list: ['Full commercial insurance included','Accidental cover','Engine & gear protection (add-on)','Does not cover negligent driving or off-roading']},
    {icon: FileWarning, t: 'Terms & Conditions', list: ['Vehicle to be used only by listed driver(s)','GPS tracking active for safety','Extra km charges apply','Late return charges apply','Company reserves the right to cancel booking','Vehicle misuse leads to immediate termination']},
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Policies & Terms</div>
        <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Everything You <span className="text-brand-orange">Need to Know</span></h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((it, i) => <Card key={i} icon={it.icon} title={it.t}>{it.list}</Card>)}
      </div>
    </section>
  );
}

function IncludedNotIncluded() {
  return (
    <section className="bg-brand-grey py-16">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Transparency</div>
          <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">What's Included in Your <span className="text-brand-orange">Self-Drive Rental</span></h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl p-6 border border-emerald-200">
            <div className="font-poppins font-bold text-emerald-700 text-[15px] mb-4 flex items-center gap-2"><CheckCircle2 size={18}/>INCLUDED</div>
            <ul className="space-y-2 text-brand-blue/80 text-[14px]">
              {['Comprehensive Insurance Coverage','24/7 Roadside Assistance','GPS Navigation Device','Basic Tool Kit & Accessories','GST Invoice Provided','Free Cancellation (72 hrs prior)','No Hidden Charges','Sanitized & Cleaned Vehicle'].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0"/>{x}</li>)}
            </ul>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-rose-200">
            <div className="font-poppins font-bold text-rose-700 text-[15px] mb-4 flex items-center gap-2"><XCircle size={18}/>NOT INCLUDED</div>
            <ul className="space-y-2 text-brand-blue/80 text-[14px]">
              {['Fuel Charges (pay as you go)','Toll & Parking Charges','Traffic Challans','Extra KM Charges (if exceeded)','Additional Driver Fee (if needed)','Off-Road Damage (not covered by insurance)'].map(x => <li key={x} className="flex gap-2"><XCircle size={15} className="text-rose-500 mt-0.5 shrink-0"/>{x}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function RoutesSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Popular Routes</div>
        <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Most Popular <span className="text-brand-orange">Self-Drive Routes</span></h2>
        <p className="text-brand-blue/60 text-[13.5px] mt-2">Every road is a story. Pick your adventure.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SELF_DRIVE_ROUTES.map((r, i) => {
          const Icon = ROUTE_ICONS[r.icon] || Mountain;
          return (
            <div key={i} className="bg-white rounded-2xl p-5 border border-brand-greyDark shadow-card card-hover">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-brand-orange/10 text-brand-orange flex items-center justify-center"><Icon size={18}/></div>
                <div className="text-[11px] font-bold text-brand-blue/55 bg-brand-grey px-2 py-1 rounded-md">{r.distance}</div>
              </div>
              <h3 className="font-poppins font-bold text-brand-blue text-[17px] leading-tight">{r.from} <span className="text-brand-blue/40">→</span> {r.to}</h3>
              <div className="flex gap-3 text-[12px] text-brand-blue/60 mt-2">
                <span className="flex items-center gap-1"><Clock size={11}/>{r.days}</span>
                <span className={`px-2 py-0.5 rounded font-semibold text-[10.5px] uppercase ${r.difficulty === 'Very High' || r.difficulty === 'High' ? 'bg-rose-100 text-rose-700' : r.difficulty === 'Moderate' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.difficulty}</span>
              </div>
              <p className="text-brand-blue/70 text-[13px] mt-3 leading-relaxed">{r.highlights}</p>
              <div className="mt-3 pt-3 border-t border-brand-grey flex items-center justify-between text-[12.5px]">
                <div className="text-brand-blue/70"><Car size={12} className="inline mr-1"/>Recommended: <span className="font-semibold text-brand-blue">{r.vehicle}</span></div>
              </div>
              <a href="#sd-booking" className="block mt-3 text-center bg-brand-blue hover:bg-brand-blueLight text-white font-semibold text-[13px] py-2.5 rounded-lg transition-colors">Plan This Route →</a>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CredentialsSection() {
  const items = [
    {icon: Award, t: 'Ministry of Tourism Authorized', d: 'Officially certified, not just another rental company'},
    {icon: Mountain, t: 'HP Tourism Registered', d: 'Recognized by Himachal Pradesh Tourism Board'},
    {icon: FileText, t: 'GST Registered', d: 'Proper billing, legit business, full transparency'},
    {icon: Star, t: 'Google 5-Star Rated', d: '500+ verified Google reviews from real customers'},
    {icon: ShieldCheck, t: 'Fully Insured Vehicles', d: 'Every car has comprehensive insurance coverage'},
    {icon: Headphones, t: '24/7 Support on Road', d: "We're always a call away, even at 3am on Rohtang"},
  ];
  return (
    <section className="bg-brand-blue text-white py-16 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 10% 20%, #FF6B00 0%, transparent 35%), radial-gradient(circle at 90% 80%, #13345b 0%, transparent 40%)'}}/>
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Our Credentials</div>
          <h2 className="font-poppins text-[28px] lg:text-[36px] leading-[1.1]">Why Himachal Travelers <span className="text-brand-orange">Trust Us</span></h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((x, i) => (
            <div key={i} className="glass rounded-2xl p-5">
              <div className="w-11 h-11 rounded-xl bg-brand-orange text-white flex items-center justify-center"><x.icon size={20}/></div>
              <div className="font-poppins font-semibold text-[15.5px] mt-3">{x.t}</div>
              <div className="text-white/70 text-[13px] mt-1.5">{x.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const reviews = [
    { initials: 'RS', name: 'Rohit Sharma', city: 'Delhi', trip: 'Manali to Spiti Valley - July 2024', text: 'Booked a Fortuner for the Spiti circuit. The car was in perfect condition - handled every mountain road brilliantly. Best self-drive experience ever!' },
    { initials: 'NK', name: 'Neha & Karan Malhotra', city: 'Pune', trip: 'Shimla to Kasauli - December 2024', text: 'Our first self-drive trip and Mountains Chain Travel made it seamless. The Swift was clean, fuel-efficient, and the GPS was a lifesaver on those winding roads. Highly recommend!' },
    { initials: 'TK', name: 'The Kapoor Family', city: 'Mumbai', trip: 'Dharamshala Circuit - October 2024', text: 'Rented an Innova Crysta for 5 days. Spacious, comfortable, perfect for our family of 6. Got 24/7 support when we needed directions. Will book again!' },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Customer Stories</div>
        <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">What Our <span className="text-brand-orange">Self-Drive Customers</span> Say</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {reviews.map((r, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-brand-greyDark shadow-card">
            <div className="flex text-brand-orange">{'★'.repeat(5).split('').map((s, j) => <span key={j} className="text-[18px]">{s}</span>)}</div>
            <p className="text-brand-blue/85 text-[14px] mt-3 leading-relaxed">"{r.text}"</p>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-brand-grey">
              <div className="w-10 h-10 rounded-full bg-brand-blue text-brand-orange font-poppins font-bold flex items-center justify-center text-[13px]">{r.initials}</div>
              <div>
                <div className="font-semibold text-brand-blue text-[13.5px]">{r.name}, {r.city}</div>
                <div className="text-[11.5px] text-brand-blue/55">{r.trip}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PickupSection() {
  return (
    <section className="bg-brand-grey py-16">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">Pickup Points</div>
        <h2 className="font-poppins text-[28px] lg:text-[34px] text-brand-blue leading-[1.1]">Available Self-Drive <span className="text-brand-orange">Pickup Locations</span></h2>
        <p className="text-brand-blue/60 text-[13.5px] mt-2">Pick up your car from any major city in Himachal Pradesh</p>
        <div className="flex flex-wrap gap-2 justify-center mt-7">
          {SELF_DRIVE_PICKUPS.map(p => (
            <a key={p} href="#sd-booking" className="px-3.5 py-2 rounded-full bg-white border border-brand-greyDark text-brand-blue text-[13px] font-semibold hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors flex items-center gap-1.5">
              <MapPin size={12}/>{p}
            </a>
          ))}
        </div>
        <p className="text-brand-blue/55 text-[12.5px] mt-5">Don't see your city? Call us - we'll arrange delivery 🚗</p>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="max-w-4xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">FAQ</div>
        <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Self-Drive Car Rental <span className="text-brand-orange">FAQ</span></h2>
      </div>
      <Accordion type="single" collapsible className="w-full">
        {SELF_DRIVE_FAQS.map((f, i) => (
          <AccordionItem key={i} value={`sd-${i}`} className="border-brand-greyDark">
            <AccordionTrigger className="text-left text-brand-blue font-semibold text-[15px] hover:text-brand-orange hover:no-underline py-5">{f.q}</AccordionTrigger>
            <AccordionContent className="text-brand-blue/70 text-[14.5px] leading-relaxed pb-5">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="max-w-7xl mx-auto px-6 mb-4">
      <div className="relative overflow-hidden rounded-3xl bg-brand-blue p-10 lg:p-12 text-center">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-brand-orange/15 blur-3xl"/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full text-[12px] text-white font-medium mb-4">
            <Sparkles size={13} className="text-brand-orange"/>Limited Vehicles Available
          </div>
          <h2 className="font-poppins font-bold text-white text-[28px] lg:text-[40px] leading-[1.1]">Ready to Drive the <span className="text-brand-orange">Himalayas?</span></h2>
          <p className="text-white/75 mt-3 text-[14.5px] max-w-xl mx-auto">Book your self-drive car today. Instant confirmation. No hidden charges.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a href="#sd-booking"><Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-6 rounded-xl font-semibold btn-glow"><Car size={17} className="mr-2"/>Book Your Self-Drive Car</Button></a>
            <a href={`tel:${COMPANY.phone}`}><Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-brand-blue h-12 px-6 rounded-xl font-semibold"><Phone size={17} className="mr-2"/>{COMPANY.phone}</Button></a>
            <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m interested in renting a self-drive car for my Himachal trip.\n\nPlease share:\n- Available vehicle options\n- Rental rates & terms\n- Pickup locations\n\nThank you!')}`} target="_blank" rel="noreferrer"><Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-brand-blue h-12 px-6 rounded-xl font-semibold"><MessageCircle size={17} className="mr-2"/>WhatsApp Us</Button></a>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 justify-center text-[12.5px] text-white/75">
            <span>Rates from ₹1,800/Day</span><span>•</span>
            <span>Min. 1 Day Rental</span><span>•</span>
            <span>Pickup from Major HP Cities</span><span>•</span>
            <span>Free Cancellation 72hrs Prior</span><span>•</span>
            <span>Fully Insured Vehicles</span>
          </div>
        </div>
      </div>
    </section>
  );
}
